import React, { useEffect, useState, useRef } from 'react';
import { Phone, PhoneOff, AlertTriangle, LogOut, Loader2, ArrowRightCircle, X, ClipboardCopy, WifiOff } from 'lucide-react';
import { useCallContext } from '../../contexts/CallContext';
import { reliableCallManager } from '../../utils/ReliableCallManager';
import { setAccessTokenExpiry } from '../../services/apiClient';
import { useTokenValidator } from '../../hooks/useTokenValidator';
import SessionLimitModal from '../SessionLimitModal';
import '../../App.css';

const PBX_HEALTH_POLL_MS = 30000;

const Footer = () => {
  const {
    reliableCalls,
    isAuthenticated,
    isAuthLoading,
    currentUser,
    handleLoginSuccess,
    handleLogout,
    activeCall,
    connectionStatus,
    reconnectAttempts,
    MAX_RECONNECT_ATTEMPTS,
    connectWebSocket,
  } = useCallContext();
  const [activeCalls, setActiveCalls] = useState([]);
  const [pbxHealth, setPbxHealth] = useState(null);
  const [pbxHealthError, setPbxHealthError] = useState(false);
  const [extensionsError, setExtensionsError] = useState(null);
  const [diagCopied, setDiagCopied] = useState(false);

  // ─── Validate session cookie health on focus/visibility/idle ───
  useTokenValidator(isAuthenticated);
  
  // Login form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [lockoutState, setLockoutState] = useState(null);
  const [countdown, setCountdown] = useState(null);
  // Set when login is blocked by the device cap; drives the manage-devices modal.
  const [sessionLimit, setSessionLimit] = useState(null);

  useEffect(() => {
    if (!lockoutState || lockoutState.type !== 'locked' || !lockoutState.remainingTime) return;

    setCountdown(Math.ceil(lockoutState.remainingTime.ms / 1000));

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setLockoutState(null);
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [lockoutState]);

  const formatCountdown = (totalSeconds) => {
    if (!totalSeconds) return '';
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`;
  };

  const handleQuitApp = () => {
    if (window.electronAPI && window.electronAPI.quitApp) {
      window.electronAPI.quitApp();
    } else {
      window.close();
    }
  };

  // ✅ Transfer state
  const [transferring, setTransferring] = useState(false);
  const [transferResult, setTransferResult] = useState(null);
  const [transferDestination, setTransferDestination] = useState('');
  const [allExtensions, setAllExtensions] = useState([]);

  // const BASE_URL = import.meta.env.VITE_BASE_URL || 'pwa-sandbox.comstreamtech.com';
  const apiUrl = window.location.hostname === 'localhost'
    ? 'http://localhost:7080/api/login'
    : `/api/login`;

  useEffect(() => {
    // Notify Electron that the footer is ready
    if (window.electronAPI && window.electronAPI.footerReady) {
      window.electronAPI.footerReady();
    }
  }, []);

  // Lock body/html scroll and clear margins while the login view is mounted,
  // so the popup fills edge-to-edge with no scrollbar and nothing bleeds through.
  useEffect(() => {
    if (isAuthenticated || isAuthLoading) return;

    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyMargin: body.style.margin,
      bodyPadding: body.style.padding,
      htmlHeight: html.style.height,
      bodyHeight: body.style.height,
    };

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.margin = '0';
    body.style.padding = '0';
    html.style.height = '100%';
    body.style.height = '100%';

    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.margin = prev.bodyMargin;
      body.style.padding = prev.bodyPadding;
      html.style.height = prev.htmlHeight;
      body.style.height = prev.bodyHeight;
    };
  }, [isAuthenticated, isAuthLoading]);

  useEffect(() => {
    // Expand or shrink the Electron window depending on authentication state
    if (window.electronAPI && window.electronAPI.setLoginMode) {
      window.electronAPI.setLoginMode(!isAuthenticated);
    }
    
    // Fetch extensions if authenticated
    if (isAuthenticated) {
      const fetchExtensions = async () => {
        try {
          const apiUrl = window.location.hostname === 'localhost'
            ? `http://localhost:7080/api/pbx/all-extensions`
            : `/api/pbx/all-extensions`;
          const response = await fetch(apiUrl, {
            credentials: 'include'
          });
          const data = await response.json();
          if (data.success) {
            setAllExtensions(data.users || []);
            setExtensionsError(null);
          } else {
            setExtensionsError(data.message || 'Could not load extensions');
          }
        } catch (error) {
          console.error('Failed to fetch extensions:', error);
          setExtensionsError('Could not load extensions');
        }
      };
      fetchExtensions();
    }
  }, [isAuthenticated]);

  // ─── Poll 3CX upstream health so the footer can show "3CX is down" vs app-WS issues ───
  useEffect(() => {
    if (!isAuthenticated) {
      setPbxHealth(null);
      setPbxHealthError(false);
      return;
    }

    const url = window.location.hostname === 'localhost'
      ? 'http://localhost:7080/api/pbx/health'
      : '/api/pbx/health';

    let cancelled = false;

    const fetchHealth = async () => {
      try {
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setPbxHealth(data);
        setPbxHealthError(false);
      } catch (err) {
        if (cancelled) return;
        setPbxHealthError(true);
      }
    };

    fetchHealth();
    const interval = setInterval(fetchHealth, PBX_HEALTH_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isAuthenticated]);

  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const HIDE_ENDED_AFTER_MS = 4000;
    const calls = reliableCalls || [];

    const computeVisible = () => calls.filter(call => {
      if (!call.callEnded) return true;
      if (!call.endedAt) return false;
      return Date.now() - call.endedAt < HIDE_ENDED_AFTER_MS;
    });

    setActiveCalls(computeVisible());

    const pendingDelays = calls
      .filter(call => call.callEnded && call.endedAt)
      .map(call => HIDE_ENDED_AFTER_MS - (Date.now() - call.endedAt))
      .filter(ms => ms > 0);

    if (pendingDelays.length === 0) return;

    const timer = setTimeout(() => {
      setActiveCalls(computeVisible());
    }, Math.min(...pendingDelays) + 50);

    return () => clearTimeout(timer);
  }, [reliableCalls]);

  useEffect(() => {
    // Automatically auto-scroll horizontally slowly when new calls roll in
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        left: scrollContainerRef.current.scrollWidth,
        behavior: 'smooth'
      });
    }
  }, [activeCalls.length]);

  const handleFooterLogin = async (e) => {
    // Called both from the form's onSubmit and (with no event) as a retry after
    // the user frees a device slot in the session-limit modal.
    if (e) e.preventDefault();
    if (lockoutState?.type === 'locked' || lockoutState?.type === 'blocked') return;

    setIsLoggingIn(true);

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setLockoutState(null);
        setCountdown(null);

        setAccessTokenExpiry(data.expiresIn);

        const user = {
          username: data.username,
          role: data.role || 'standard',
          roleDetails: data.roleDetails
        };

        localStorage.setItem('currentUser', JSON.stringify(user));
        handleLoginSuccess(user);
      } else if (data.limitReached) {
        // Password was correct, but the device cap is hit. Keep the password in
        // state so we can retry login after the user frees a slot, and open the
        // manage-devices modal.
        setLockoutState(null);
        setSessionLimit({
          token: data.sessionMgmtToken,
          username: data.username || username,
          maxSessions: data.maxSessions || 3
        });
      } else {
        setPassword('');

        if (data.blocked) {
          setLockoutState({ type: 'blocked' });
        } else if (data.locked) {
          setLockoutState({ type: 'locked', remainingTime: data.remainingTime });
        } else {
          setLockoutState({ type: 'warning', remainingChances: data.remainingChances });
        }
      }
    } catch (error) {
      console.error('Footer Login Error:', error);
      setLockoutState({ type: 'warning', remainingChances: null, isNetworkError: true });
    } finally {
      setIsLoggingIn(false);
    }
  };

  const isFormDisabled = isLoggingIn
    || lockoutState?.type === 'locked'
    || lockoutState?.type === 'blocked';

  const renderStatusBannerContent = () => {
    if (!lockoutState) return null;

    if (lockoutState.type === 'blocked') {
      return (
        <div className="login-status-banner login-status--blocked">
          <span className="login-status-icon">🚫</span>
          <div>
            <strong>Account Permanently Blocked</strong>
            <p>Contact your administrator to restore access.</p>
          </div>
        </div>
      );
    }

    if (lockoutState.type === 'locked') {
      return (
        <div className="login-status-banner login-status--locked">
          <span className="login-status-icon">🔒</span>
          <div>
            <strong>Account Temporarily Locked</strong>
            <p>
              Try again in{' '}
              <span className="lockout-countdown">{formatCountdown(countdown)}</span>
            </p>
          </div>
        </div>
      );
    }

    if (lockoutState.type === 'warning') {
      if (lockoutState.isNetworkError) {
        return (
          <div className="login-status-banner login-status--warning">
            <span className="login-status-icon">⚠️</span>
            <div>
              <strong>Connection Error</strong>
              <p>Please check your connection and try again.</p>
            </div>
          </div>
        );
      }

      const isLastChance = lockoutState.remainingChances === 1;
      return (
        <div className={`login-status-banner ${isLastChance ? 'login-status--danger' : 'login-status--warning'}`}>
          <span className="login-status-icon">{isLastChance ? '⛔' : '⚠️'}</span>
          <div>
            <strong>Incorrect Password</strong>
            <p>
              {isLastChance
                ? 'Last attempt — next failure locks your account for 1 hour.'
                : `${lockoutState.remainingChances} attempts remaining before account lockout.`}
            </p>
            <div className="attempt-pips">
              {[...Array(3)].map((_, i) => (
                <span
                  key={i}
                  className={`pip ${i < (3 - lockoutState.remainingChances) ? 'pip--used' : 'pip--remaining'}`}
                />
              ))}
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const hasConnectedCall = activeCalls.some(call => 
    !call.callEnded && (call.timerStarted || call.status === 'Connected' || call.status === 'Talking')
  );

  // Show loading spinner while verifying session with server
  if (isAuthLoading) {
    return (
      <div className="h-screen w-full bg-[#111827] flex items-center justify-center text-white">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          <span className="text-gray-400 text-sm">Verifying session…</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
      {/* Rendered OUTSIDE the draggable login card and marked no-drag, otherwise
          Electron would swallow clicks on the modal as window drags. */}
      {sessionLimit && (
        <div style={{ WebkitAppRegion: 'no-drag' }}>
          <SessionLimitModal
            token={sessionLimit.token}
            username={sessionLimit.username}
            maxSessions={sessionLimit.maxSessions}
            onClose={() => setSessionLimit(null)}
            onResolved={() => { setSessionLimit(null); handleFooterLogin(); }}
          />
        </div>
      )}
      <div className="login-container" style={{ WebkitAppRegion: 'drag', overflow: 'hidden', height: '100vh', position: 'relative' }}>
        <button
          type="button"
          onClick={handleQuitApp}
          title="Close"
          style={{
            position: 'absolute',
            top: 10,
            right: 12,
            zIndex: 10,
            WebkitAppRegion: 'no-drag',
            width: 32,
            height: 32,
            borderRadius: '50%',
            border: 'none',
            background: 'rgba(255,255,255,0.18)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'background 0.15s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#ef4444'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}
        >
          <X size={16} />
        </button>
        <div className="login-card" style={{ WebkitAppRegion: 'no-drag', overflow: 'hidden' }}>
          <div className="login-form-section">
            <div className="company-branding">
              <div className="company-name" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <img
                  src="/logo/logo-no-bg.webp"
                  alt="Bozarth Connect"
                  style={{
                    height: '96px',
                    width: '96px',
                    minHeight: '96px',
                    minWidth: '96px',
                    objectFit: 'contain',
                    flexShrink: 0
                  }}
                />
                <div>
                  <h1 className="company-name-main">BOZARTH</h1>
                  <p className="company-name-sub">Connect</p>
                </div>
              </div>
            </div>
            <div className="login-header">
              <h2>Login</h2>
            </div>

            {/* ── Fixed-height slot: always reserves space, never shifts layout ── */}
            <div className="status-banner-slot">
              {renderStatusBannerContent()}
            </div>

            <form onSubmit={handleFooterLogin} className="login-form">
              <div className="form-group">
                <label htmlFor="username">Extension</label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); setLockoutState(null); }}
                  placeholder="Enter your extension"
                  required
                  disabled={isFormDisabled}
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-input-wrapper">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    disabled={isFormDisabled}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                    disabled={isFormDisabled}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div className="form-options">
                <label className="remember-me">
                  <input type="checkbox" />
                  <span>Remember Me</span>
                </label>
              </div>

              <button
                type="submit"
                className={`btn-login ${lockoutState?.type === 'locked' || lockoutState?.type === 'blocked' ? 'btn-login--disabled' : ''}`}
                disabled={isFormDisabled}
              >
                {isLoggingIn
                  ? 'Signing in...'
                  : lockoutState?.type === 'locked'
                    ? `Locked (${formatCountdown(countdown)})`
                    : lockoutState?.type === 'blocked'
                      ? 'Account Blocked'
                      : 'LOGIN'}
              </button>
            </form>
          </div>
          <div className="login-welcome-section">
            <div className="welcome-content">
              <h1>Welcome<br />Back.</h1>
              <p>Login to your call tracking dashboard and stay connected. Quick access to all your customer interactions.</p>
            </div>
          </div>
        </div>
      </div>
      </>
    );
  }

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'Unknown';
    const cleaned = phone.toString().replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  // ─── Derive footer connection banner ───
  // Distinguishes: app↔backend WS issues vs upstream 3CX (REST/PBX-WS) issues vs network offline.
  // const getConnectionBanner = () => {
  //   if (typeof navigator !== 'undefined' && navigator.onLine === false) {
  //     return { tone: 'red', label: 'Offline', detail: 'Your device is not connected to the internet.' };
  //   }
  //   if (connectionStatus === 'failed') {
  //     return { tone: 'red', label: 'Disconnected from server', detail: 'Tap Retry to reconnect.', retry: true };
  //   }
  //   if (connectionStatus === 'reconnecting') {
  //     return {
  //       tone: 'amber',
  //       label: `Reconnecting (${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`,
  //       detail: 'Live call updates may be delayed.'
  //     };
  //   }
  //   if (connectionStatus === 'timeout') {
  //     return { tone: 'amber', label: 'Connection timeout', detail: 'Live call updates may be delayed.', retry: true };
  //   }
  //   if (connectionStatus === 'disconnected') {
  //     return { tone: 'amber', label: 'Reconnecting…', detail: 'Live call updates may be delayed.' };
  //   }
  //   if (pbxHealthError) {
  //     return { tone: 'amber', label: '3CX status unknown', detail: 'Could not reach health check.' };
  //   }
  //   if (pbxHealth && pbxHealth.status === 'down') {
  //     return { tone: 'red', label: '3CX is down', detail: 'Calls cannot be made or transferred. Contact support.' };
  //   }
  //   if (pbxHealth && pbxHealth.status === 'degraded') {
  //     const restDown = !pbxHealth.rest?.ok;
  //     const wsDown = !pbxHealth.ws?.ok;
  //     const detail = restDown && wsDown ? '3CX REST and event stream both unavailable.'
  //       : restDown ? '3CX call control (REST) unavailable.'
  //       : wsDown ? '3CX event stream unavailable — call updates may be delayed.'
  //       : 'Partial 3CX availability.';
  //     return { tone: 'amber', label: '3CX degraded', detail };
  //   }
  //   if (extensionsError) {
  //     return { tone: 'amber', label: 'Limited features', detail: extensionsError + ' — transfer suggestions unavailable.' };
  //   }
  //   return null;
  // };

  // const handleCopyDiagnostics = async () => {
  //   const diag = {
  //     timestamp: new Date().toISOString(),
  //     user: currentUser?.username || null,
  //     extension: currentUser?.roleDetails?.extension || null,
  //     url: typeof window !== 'undefined' ? window.location.href : null,
  //     online: typeof navigator !== 'undefined' ? navigator.onLine : null,
  //     userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  //     appWebSocket: {
  //       status: connectionStatus,
  //       reconnectAttempts,
  //       maxReconnectAttempts: MAX_RECONNECT_ATTEMPTS,
  //     },
  //     pbxHealth: pbxHealth || null,
  //     pbxHealthError,
  //     extensionsError,
  //     activeCallCount: activeCalls.length,
  //   };
  //   const text = JSON.stringify(diag, null, 2);
  //   try {
  //     if (navigator?.clipboard?.writeText) {
  //       await navigator.clipboard.writeText(text);
  //     } else {
  //       const ta = document.createElement('textarea');
  //       ta.value = text;
  //       document.body.appendChild(ta);
  //       ta.select();
  //       document.execCommand('copy');
  //       document.body.removeChild(ta);
  //     }
  //     setDiagCopied(true);
  //     setTimeout(() => setDiagCopied(false), 2000);
  //   } catch (err) {
  //     console.error('Failed to copy diagnostics:', err);
  //   }
  // };

  // const bannerToneClasses = (tone) => {
  //   if (tone === 'red') return 'bg-red-900/40 text-red-100 border-red-500/40';
  //   if (tone === 'amber') return 'bg-amber-900/40 text-amber-100 border-amber-500/40';
  //   return 'bg-gray-800/40 text-gray-200 border-gray-500/40';
  // };

  const getCustomerName = (call) => {
    // Priority: Tekion → CRM (Vinsolutions) → Twilio
    const td = call.customerData?.tekionData;
    if (td && td.customerId) {
      if (td.firstName || td.lastName) {
        return `${td.firstName || ''} ${td.lastName || ''}`.trim();
      }
      if (td.displayName) return td.displayName;
    }
    if (call.customerData?.contact?.firstName) {
      return `${call.customerData.contact.firstName} ${call.customerData.contact.lastName || ''}`.trim();
    }
    if (call.customerData?.twilioData?.callerName) {
      return call.customerData.twilioData.callerName;
    }
    return call.callerName || 'Unknown Caller';
  };

  // const banner = getConnectionBanner();

  return (
    // h-screen (not a fixed px height) so the bar always matches the Electron
    // window's FOOTER_HEIGHT exactly — border-box keeps the 1px top border
    // inside the 100vh, which is what used to push it 1px over and show a
    // vertical scrollbar.
    <div className="h-screen w-full bg-[#1c2533] border-t border-[#2d3748] shadow-2xl overflow-hidden">
      {/* {banner && (
        <div className={`flex items-center justify-between gap-3 px-6 py-1.5 text-xs border-b ${bannerToneClasses(banner.tone)}`}>
          <div className="flex items-center gap-2 min-w-0">
            <WifiOff className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="font-bold uppercase tracking-wider whitespace-nowrap">{banner.label}</span>
            <span className="opacity-90 truncate">{banner.detail}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {banner.retry && (
              <button
                onClick={() => connectWebSocket && connectWebSocket()}
                className="px-2 py-0.5 rounded bg-white/10 hover:bg-white/20 text-white text-[11px] font-semibold"
              >
                Retry
              </button>
            )}
            <button
              onClick={handleCopyDiagnostics}
              className="flex items-center gap-1 px-2 py-0.5 rounded bg-white/10 hover:bg-white/20 text-white text-[11px] font-semibold"
              title="Copy diagnostics for support"
            >
              <ClipboardCopy className="w-3 h-3" />
              {diagCopied ? 'Copied' : 'Copy diagnostics'}
            </button>
          </div>
        </div>
      )} */}
      <div className="h-full w-full flex items-center px-6 overflow-hidden">
      {/* Left Section: Header */}
      <div className="flex items-center gap-4 border-r border-[#4a5568] pr-6 mr-6 flex-shrink-0">
        <div className="relative">
          <div className="p-2 bg-[#2d3748] rounded-full">
            <Phone className="w-6 h-6 text-[#ef4444] fill-[#ef4444]" />
          </div>
          {activeCalls.length > 0 && (
            <div className="absolute -top-1 -right-1 bg-[#ef4444] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-[#1c2533]">
              {activeCalls.length}
            </div>
          )}
        </div>
        <div>
          <span className="text-white font-black text-1xl tracking-tight whitespace-nowrap leading-none block">Live Calls</span>
          <div className="flex items-center gap-1.5 mt-1 opacity-60">
            <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
            <span className="text-white text-[9px] uppercase font-bold tracking-widest">{currentUser?.username || 'Active'}</span>
          </div>
        </div>
      </div>

      <style>{`
        .call-cards-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.25) transparent;
        }
        .call-cards-scroll::-webkit-scrollbar {
          height: 6px;
        }
        .call-cards-scroll::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.04);
          border-radius: 3px;
        }
        .call-cards-scroll::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.25);
          border-radius: 3px;
        }
        .call-cards-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.4);
        }
      `}</style>

      {/* Middle Section: Call Cards */}
      <div
        ref={scrollContainerRef}
        className="flex-1 flex gap-3 overflow-x-auto call-cards-scroll py-1 items-center scroll-smooth"
      >
        {activeCalls.length === 0 ? (
          <div className="text-gray-500 text-sm flex items-center italic gap-2 pl-2">
            <div className="w-2 h-2 bg-gray-600 rounded-full animate-pulse"></div>
            Waiting for calls to ext {currentUser?.username}...
          </div>
        ) : (
          activeCalls.map((call) => (
            <FooterCallCard 
              key={call.callId || call.id} 
              call={call} 
              formatPhoneNumber={formatPhoneNumber}
              getCustomerName={getCustomerName}
              allExtensions={allExtensions}
              currentUser={currentUser}
            />
          ))
        )}
      </div>

      {/* Right Section: Actions */}
            <div className="ml-6 pl-6 border-l border-[#4a5568] flex items-center gap-4">
              {window.electronAPI && (
                <button
                  onClick={() => window.electronAPI.openDashboard('/')}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600/20 hover:bg-blue-600 text-blue-400 hover:text-white border border-blue-500/30 rounded-lg transition-all text-xs font-bold whitespace-nowrap"
                >
                  <ArrowRightCircle className="w-4 h-4" />
                  View All Calls
                </button>
              )}
              <button
                onClick={handleLogout}
                className="p-2.5 rounded-full hover:bg-white/5 text-gray-400 hover:text-white transition-all group relative ml-auto"
                title="Logout"
              >
                <LogOut className="w-5 h-4" />
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                  Logout ({currentUser?.username})
                </span>
              </button>
              <button
                onClick={handleQuitApp}
                className="p-2.5 rounded-full hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-all group relative"
                title="Close"
              >
                <X className="w-5 h-5" />
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                  Close App
                </span>
              </button>
            </div>
      </div>
    </div>
  );
};

const FooterCallCard = ({ call, formatPhoneNumber, getCustomerName, allExtensions, currentUser }) => {
  const [duration, setDuration] = useState(0);
  const [showTransferInput, setShowTransferInput] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [transferResult, setTransferResult] = useState(null);
  const [transferDestination, setTransferDestination] = useState('');
  
  const [calling, setCalling] = useState(false);
  const [callError, setCallError] = useState(null);
  const [endingCall, setEndingCall] = useState(false);
  const [endCallResult, setEndCallResult] = useState(null);
  const userExtension = currentUser?.roleDetails?.extension || currentUser?.username;

  const isEnded = call.callEnded || call.status === 'Ended';
  const isConnected = !isEnded && (call.timerStarted || call.status === 'Connected' || call.status === 'Talking');
  const isRinging = !isEnded && !isConnected;

  // Resolve the user's own participant ID so the drop hits the right leg of the call
  const getUserParticipantId = () => {
    if (!Array.isArray(call.participants) || call.participants.length === 0) return null;

    const userExt = String(userExtension || '');

    const ownParticipant = call.participants.find(
      p => String(p.dn) === userExt && p.participant_id
    );
    if (ownParticipant?.participant_id) return ownParticipant.participant_id;

    const connected = call.participants.find(
      p => (p.status === 'Connected' || p.status === 'Talking') && p.participant_id
    );
    if (connected?.participant_id) return connected.participant_id;

    const any = call.participants.find(p => p.participant_id);
    return any?.participant_id || null;
  };

  const canEndCall = !isEnded && !!getUserParticipantId();

  const handleEndCall = async (e) => {
    e.stopPropagation();
    if (endingCall) return;

    const participantId = getUserParticipantId();
    if (!participantId) {
      setEndCallResult({ success: false, message: 'No participant' });
      setTimeout(() => setEndCallResult(null), 3000);
      return;
    }

    setEndingCall(true);
    setEndCallResult(null);

    try {
      const apiUrl = window.location.hostname === 'localhost'
        ? `http://localhost:7080/api/pbx/drop/${participantId}`
        : `/api/pbx/drop/${participantId}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'None' })
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        setEndCallResult({ success: true, message: 'Ended' });
      } else {
        setEndCallResult({ success: false, message: data.message || `Failed (HTTP ${response.status})` });
      }
    } catch (error) {
      console.error('❌ End call error:', error);
      setEndCallResult({ success: false, message: 'Network error — could not reach server' });
    } finally {
      setEndingCall(false);
      setTimeout(() => setEndCallResult(null), 5000);
    }
  };

  // ✅ Transfer call handler
  const handleTransfer = async () => {
    const cleanDest = transferDestination.trim();
    if (!cleanDest) {
      setTransferResult({ success: false, message: 'Enter number' });
      setTimeout(() => setTransferResult(null), 3000);
      return;
    }

    setTransferring(true);
    setTransferResult(null);

    try {
      // Find participant ID for this specific call
      const participant = call.participants?.find(p =>
        p.status === 'Connected' || p.status === 'Talking'
      );
      
      const participantId = participant?.participant_id;

      if (!participantId) {
        setTransferResult({ success: false, message: 'Call not connected' });
        setTransferring(false);
        setTimeout(() => setTransferResult(null), 3000);
        return;
      }

      const apiUrl = window.location.hostname === 'localhost'
        ? `http://localhost:7080/api/pbx/transfer/${participantId}`
        : `/api/pbx/transfer/${participantId}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          destination: cleanDest,
          timeout: 30
        })
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        setTransferResult({ success: true, message: 'Transferred' });
        setTransferDestination('');
        setTimeout(() => setShowTransferInput(false), 2000);
      } else {
        setTransferResult({ success: false, message: data.message || `Failed (HTTP ${response.status})` });
      }
    } catch (error) {
      console.error('❌ Transfer error:', error);
      setTransferResult({ success: false, message: 'Network error — could not reach server' });
    } finally {
      setTransferring(false);
      setTimeout(() => setTransferResult(null), 6000);
    }
  };

  const handleCall3CX = async () => {
    const phoneNumber = call.phoneNumber || call.callerNumber;
    if (!phoneNumber) return;
    
    let cleanPhone = phoneNumber.replace(/\D/g, '');
    if (cleanPhone.length === 11 && cleanPhone.startsWith('1')) {
      cleanPhone = cleanPhone.substring(1);
    }

    if (!userExtension) {
        setCallError('Ext. not configured');
        setTimeout(() => setCallError(null), 3000);
        return;
    }

    setCalling(true);
    setCallError(null);

    const apiUrl = window.location.hostname === 'localhost'
        ? `http://localhost:7080/api/pbx/click-to-call/${cleanPhone}`
        : `/api/pbx/click-to-call/${cleanPhone}`;

    try {
        const response = await fetch(apiUrl, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                timeout: 30,
                attacheddata: {}
            })
        });

        const data = await response.json().catch(() => ({}));

        if (response.ok && data.success) {
            console.log('✅ Call initiated successfully via 3CX');
        } else {
            console.error('❌ Failed to initiate call:', data.message);
            setCallError(data.message || `Failed (HTTP ${response.status})`);
        }
    } catch (error) {
        console.error('❌ Error calling 3CX API:', error);
        setCallError('Network error — could not reach server');
    } finally {
        setCalling(false);
        setTimeout(() => setCallError(null), 5000);
    }
  };

  useEffect(() => {
    let interval;
    if (isEnded) {
      if (call.callDuration) {
        setDuration(call.callDuration);
      }
      return;
    }

    // For reliable calls, focus on startTime (connectedAt) or firstSeen
    const startTimeSource = call.startTime || call.ringStartTime || call.timestamp || call.firstSeen;
    
    if (startTimeSource) {
      const update = () => {
        const diff = Math.floor((Date.now() - new Date(startTimeSource)) / 1000);
        setDuration(Math.max(0, diff));
      };
      update();
      interval = setInterval(update, 1000);
    }
    
    return () => clearInterval(interval);
  }, [call.startTime, call.ringStartTime, call.timestamp, call.firstSeen, isEnded, call.callDuration]);

  const formatTime = (secs) => {
    const mins = Math.floor(secs / 60);
    const s = secs % 60;
    return `${mins.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // Determine card background and status label
  const bgColor = isEnded ? 'bg-gray-700/80 saturate-50' : isRinging ? 'bg-[#ef4444]' : isConnected ? 'bg-[#10b981]' : 'bg-[#2d3748]';
  const statusLabel = isEnded ? 'Ended' : isRinging ? 'Ringing' : isConnected ? 'Connected' : 'Active';

  return (
    <div 
      className={`flex-shrink-0 w-[300px] h-[44px] rounded-md flex items-center px-3 transition-all ${bgColor} border border-white/10 shadow-md`}
    >
      <div className="flex-1 min-w-0 pr-2">
        <div className="flex items-center gap-1.5 mb-0.5">
          <h4 className="text-white font-bold text-[13px] truncate leading-none" title={getCustomerName(call)}>
            {getCustomerName(call)}
          </h4>
          {isRinging && <AlertTriangle className="w-3 h-3 text-yellow-400 fill-yellow-400 flex-shrink-0" />}
        </div>
        {(callError || endCallResult) ? (
          <p
            className={`text-[10px] font-bold leading-none truncate ${
              endCallResult?.success ? 'text-emerald-300' : 'text-red-300'
            }`}
            title={callError || endCallResult?.message}
          >
            {callError || endCallResult?.message}
          </p>
        ) : (
          <p className="text-white text-[11px] opacity-80 truncate leading-none" title={formatPhoneNumber(call.phoneNumber || call.callerNumber)}>
            {formatPhoneNumber(call.phoneNumber || call.callerNumber)}
          </p>
        )}
      </div>
      
      <div className="flex items-center gap-2 border-l border-white/20 pl-2 flex-shrink-0">
        {showTransferInput ? (
          <div className="flex items-center gap-1.5 transition-all">
            {transferResult ? (
              <span
                className={`text-[10px] font-bold px-2 py-1 rounded bg-black/20 max-w-[140px] truncate ${
                  transferResult.success ? 'text-emerald-400' : 'text-red-400'
                }`}
                title={transferResult.message}
              >
                {transferResult.message}
              </span>
            ) : (
              <>
                <input 
                  autoFocus
                  type="text"
                  list={`card-extension-list-${call.callId || call.id}`}
                  placeholder="Ext..."
                  className="w-16 bg-white/10 border-none outline-none text-white text-[11px] px-1.5 py-1 rounded placeholder:text-white/40"
                  value={transferDestination}
                  onChange={(e) => setTransferDestination(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleTransfer();
                    if (e.key === 'Escape') setShowTransferInput(false);
                  }}
                />
                <datalist id={`card-extension-list-${call.callId || call.id}`}>
                  {allExtensions?.map(ext => (
                    <option key={ext.Number} value={ext.Number}>{ext.FirstName} {ext.LastName}</option>
                  ))}
                </datalist>
                <button 
                  onClick={handleTransfer}
                  disabled={transferring}
                  className="p-1 bg-blue-500 rounded text-white hover:bg-blue-600 transition-colors"
                >
                  {transferring ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRightCircle className="w-3 h-3" />}
                </button>
                <button 
                  onClick={() => setShowTransferInput(false)}
                  className="text-[10px] text-white/50 hover:text-white px-1"
                >
                  ✕
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="text-right min-w-[40px]">
              <p className="text-white text-[9px] font-bold opacity-70 uppercase tracking-tighter leading-none mb-0.5">
                {statusLabel}
              </p>
              <p className="text-white text-[13px] font-black leading-none font-mono">
                {formatTime(duration)}
              </p>
            </div>
            
            <div className="flex gap-1">
              {!isEnded ? (
                <>
                  <button
                    onClick={handleEndCall}
                    disabled={!canEndCall || endingCall}
                    className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${
                      endingCall || !canEndCall
                        ? 'bg-gray-500/50 cursor-not-allowed'
                        : isRinging
                          ? 'bg-black/20 hover:bg-black/40'
                          : 'bg-red-500 hover:bg-red-600'
                    }`}
                    title={!canEndCall ? 'Cannot end call' : endingCall ? 'Ending…' : 'End Call'}
                  >
                    {endingCall ? (
                      <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
                    ) : (
                      <PhoneOff className="w-3.5 h-3.5 text-white" />
                    )}
                  </button>
                  {isConnected && (
                    <button
                      onClick={() => setShowTransferInput(true)}
                      className="w-7 h-7 bg-blue-500 hover:bg-blue-600 rounded flex items-center justify-center transition-colors shadow-sm"
                      title="Transfer Call"
                    >
                      <ArrowRightCircle className="w-3.5 h-3.5 text-white" />
                    </button>
                  )}
                </>
              ) : (
                <button 
                  onClick={handleCall3CX}
                  disabled={calling || !userExtension}
                  className={`w-7 h-7 rounded flex items-center justify-center transition-colors shadow-sm ${
                    calling || !userExtension ? 'bg-gray-500/50 cursor-not-allowed' : 'bg-emerald-500 hover:bg-emerald-600'
                  }`}
                  title={!userExtension ? 'Extension not configured' : 'Call via 3CX'}
                >
                  {calling ? (
                     <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
                  ) : (
                     <Phone className="w-3.5 h-3.5 text-white fill-white" />
                  )}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Footer;